import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { Banknote, CheckCircle2, CreditCard, Printer, Smartphone } from 'lucide-react'
import type { OnlineSubMethod } from '@/types'
import type { GuestShare } from '@/lib/splitBilling'

export interface SplitGuestCardProps {
  share: GuestShare
  /** exact amount this guest's tender will record (may differ from share.total for the last guest) */
  payableAmount: number
  mode: 'items' | 'even'
  guestCount: number
  cardOnly: boolean
  onlineSubMethods: OnlineSubMethod[]
  processing: boolean
  /** true while another guest's tender is mid-flight — pay disabled */
  payLocked: boolean
  formatCurrency: (n: number) => string
  onPay: (tender: 'cash' | 'card' | 'online', subMethod?: string) => void
  onReprint?: () => void
}

/**
 * One guest's bill in the split dialog: their lines (or even share), their
 * total, and — until settled — an inline tender picker. Cash shows a small
 * received/change calculator; the recorded amount is always `payableAmount`.
 */
export function SplitGuestCard({
  share,
  payableAmount,
  mode,
  guestCount,
  cardOnly,
  onlineSubMethods,
  processing,
  payLocked,
  formatCurrency,
  onPay,
  onReprint,
}: SplitGuestCardProps) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [cashOpen, setCashOpen] = useState(false)
  const [received, setReceived] = useState('')

  const receivedNum = parseFloat(received || '0')
  const cashShort = !Number.isFinite(receivedNum) || receivedNum < payableAmount
  const change = Math.max(0, (Number.isFinite(receivedNum) ? receivedNum : 0) - payableAmount)

  const pay = (tender: 'cash' | 'card' | 'online', subMethod?: string) => {
    setPickerOpen(false)
    setCashOpen(false)
    setReceived('')
    onPay(tender, subMethod)
  }

  return (
    <div
      className={cn(
        'rounded-xl border p-3',
        share.paid
          ? 'border-emerald-400/60 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20'
          : 'border-border bg-card'
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold">
          {share.label}
          <span className="ml-1.5 text-xs font-normal text-muted-foreground">of {guestCount}</span>
        </span>
        <span className="text-base font-bold tabular-nums">{formatCurrency(share.total)}</span>
      </div>

      {mode === 'even' ? (
        <p className="mt-1 text-xs text-muted-foreground">Even split — 1/{guestCount} of the bill</p>
      ) : (
        <ul className="mt-1.5 space-y-0.5">
          {share.lines.map((l) => (
            <li key={l.orderItemId} className="flex justify-between gap-2 text-xs text-muted-foreground">
              <span className="min-w-0 truncate">
                {l.units > 1 ? `${l.units} × ` : ''}
                {l.name}
              </span>
              <span className="shrink-0 tabular-nums">{formatCurrency(l.amount)}</span>
            </li>
          ))}
          {share.lines.length === 0 && (
            <li className="text-xs italic text-muted-foreground">No items assigned</li>
          )}
        </ul>
      )}

      {share.paid ? (
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-4 w-4" aria-hidden />
            Paid{share.tender ? ` · ${share.tender}` : ''}
          </span>
          {onReprint && (
            <Button type="button" variant="ghost" size="sm" className="h-8 gap-1.5 px-2 text-xs" onClick={onReprint}>
              <Printer className="h-3.5 w-3.5" aria-hidden />
              Reprint slip
            </Button>
          )}
        </div>
      ) : !pickerOpen ? (
        <Button
          type="button"
          size="sm"
          className="mt-2 h-9 w-full text-sm font-semibold"
          disabled={processing || payLocked || payableAmount <= 0}
          onClick={() => setPickerOpen(true)}
        >
          Pay {formatCurrency(payableAmount)}
        </Button>
      ) : (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            <Button
              type="button"
              variant={cashOpen ? 'default' : 'outline'}
              size="sm"
              className="h-9 gap-1 text-xs font-semibold"
              disabled={cardOnly || processing}
              onClick={() => setCashOpen((v) => !v)}
            >
              <Banknote className="h-3.5 w-3.5" aria-hidden />
              Cash
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-9 gap-1 text-xs font-semibold"
              disabled={processing}
              onClick={() => pay('card')}
            >
              <CreditCard className="h-3.5 w-3.5" aria-hidden />
              Card
            </Button>
            {onlineSubMethods.length === 0 ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 gap-1 text-xs font-semibold"
                disabled={processing}
                onClick={() => pay('online')}
              >
                <Smartphone className="h-3.5 w-3.5" aria-hidden />
                Online
              </Button>
            ) : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 text-xs"
                onClick={() => {
                  setPickerOpen(false)
                  setCashOpen(false)
                }}
              >
                Back
              </Button>
            )}
          </div>

          {onlineSubMethods.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5">
              {onlineSubMethods.map((m) => (
                <Button
                  key={m.slug}
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 gap-1 text-xs font-semibold"
                  disabled={processing}
                  onClick={() => pay('online', m.slug)}
                >
                  <Smartphone className="h-3.5 w-3.5" aria-hidden />
                  {m.label}
                </Button>
              ))}
            </div>
          )}

          {cashOpen && !cardOnly && (
            <div className="space-y-1.5 rounded-lg border border-emerald-400/50 bg-emerald-50/50 p-2 dark:border-emerald-800 dark:bg-emerald-950/20">
              <Input
                inputMode="decimal"
                autoFocus
                className="h-9 text-base font-semibold tabular-nums"
                placeholder="Amount received"
                value={received}
                onChange={(e) => setReceived(e.target.value)}
              />
              <div className="flex items-center justify-between text-xs font-medium">
                <span>{cashShort ? 'Short by' : 'Change due'}</span>
                <span className="tabular-nums">
                  {formatCurrency(cashShort ? Math.max(0, payableAmount - (Number.isFinite(receivedNum) ? receivedNum : 0)) : change)}
                </span>
              </div>
              <Button
                type="button"
                size="sm"
                className="h-9 w-full bg-emerald-600 text-xs font-semibold text-white hover:bg-emerald-700"
                disabled={cashShort || processing}
                onClick={() => pay('cash')}
              >
                Complete cash · {formatCurrency(payableAmount)}
              </Button>
            </div>
          )}

          {onlineSubMethods.length === 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full text-xs"
              onClick={() => {
                setPickerOpen(false)
                setCashOpen(false)
              }}
            >
              Back
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
